import { PropsWithChildren, useEffect, useState } from 'react';

import { css } from '@compiled/react';

import { media } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';

import { ColumnLayout } from '@/libraries/design/foundations';

import { Tab } from './tab';
import { TabList } from './tab-list';
import { TabPanel } from './tab-panel';

export type TabData = {
	id?: string;
	title: string;
	iconUrl?: string;
	tabComponent: React.ReactElement;
};

export type TabSize = 'small' | 'large';

export type TabsSectionProps = PropsWithChildren<{
	readonly tabsData: TabData[];
	tabSize?: TabSize;
}>;

const getResolvedTabId = (tab: TabData, index: number): string => tab.id ?? `tab-${index + 1}`;

const getTabIdFromHash = (tabsData: TabData[]): string | undefined => {
	if (typeof window === 'undefined') {
		return undefined;
	}
	const hash = window.location.hash.slice(1);
	return tabsData.find((tab, index) => getResolvedTabId(tab, index) === hash) ? hash : undefined;
};

const sectionStyles = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.400'),
	[media.above.sm]: {
		gap: token('space.500'),
	},
	[media.above.md]: {
		gap: token('space.600'),
	},
	// Scope the mobile grid padding reset to the FIRST direct child only (the tab-list
	// ColumnLayout). Using `> :first-child` guarantees we don't reach into the
	// panel-stack ColumnLayout or any nested grids inside tab content — the
	// previous `div:nth-of-type(1)` selector matched any first-of-type div at
	// any depth, which collapsed nested grids on mobile. (when we were previously unsetting grid)
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'> :first-child': {
		[media.below.sm]: {
			paddingInline: 0,
		},
	},
});

const tabListInnerContainerStyles = css({
	marginInline: 'auto',
	maxWidth: '100%',
});

// Single-cell grid that stacks every TabPanel in the same `gridArea: 1 / 1`,
// so the active panel and inactive panels share the same screen position.
// The container auto-sizes to the tallest panel — no layout shift between
// tabs, and we get a true opacity-only cross-fade. Same idiom as
// `customer-testimonials` (panel grid: 1fr / 1fr). Spec: GWP-11578.
const panelStackStyles = css({
	display: 'grid',
	gridTemplateColumns: '1fr',
	gridTemplateRows: '1fr',
});

export const TabsSection = ({ tabsData, tabSize = 'large' }: TabsSectionProps) => {
	// Match SSR (no hash on the server) to avoid a hydration mismatch; the hash is applied after mount.
	const [activeTabId, setActiveTabId] = useState(() =>
		tabsData[0] ? getResolvedTabId(tabsData[0], 0) : undefined,
	);
	// Only focus on user selection; for hash-driven selection the browser focuses the target panel.
	const [shouldFocusActiveTab, setShouldFocusActiveTab] = useState(false);

	const handleTabSelect = (tabId: string) => {
		setShouldFocusActiveTab(true);
		setActiveTabId(tabId);
		// Update URL hash without scrolling
		window.history.replaceState(null, '', `#${tabId}`);
	};

	// Apply the deep-linked tab and listen for back/forward navigation
	useEffect(() => {
		const initialTabId = getTabIdFromHash(tabsData);
		if (initialTabId) {
			setShouldFocusActiveTab(false);
			setActiveTabId(initialTabId);
		}

		const handleHashChange = () => {
			const tabId = getTabIdFromHash(tabsData);
			if (tabId) {
				setShouldFocusActiveTab(false);
				setActiveTabId(tabId);
			}
		};

		window.addEventListener('hashchange', handleHashChange);
		return () => {
			window.removeEventListener('hashchange', handleHashChange);
		};
	}, [tabsData]);

	return (
		<section id="tabs" css={sectionStyles}>
			<ColumnLayout layout="10" tabletLayout="12">
				<div css={tabListInnerContainerStyles}>
					<TabList activeTabId={activeTabId}>
						{tabsData.map((tab, index) => {
							const resolvedId = getResolvedTabId(tab, index);
							return (
								<Tab
									key={resolvedId}
									tabId={resolvedId}
									index={index}
									title={tab.title}
									isActive={resolvedId === activeTabId}
									onTabSelect={handleTabSelect}
									tabSize={tabSize}
									focusOnActivate={shouldFocusActiveTab}
								/>
							);
						})}
					</TabList>
				</div>
			</ColumnLayout>
			<ColumnLayout layout="12">
				<div css={panelStackStyles}>
					{tabsData.map((tab, index) => {
						const resolvedId = getResolvedTabId(tab, index);
						return (
							<TabPanel id={resolvedId} key={resolvedId} isActive={resolvedId === activeTabId}>
								{tab.tabComponent}
							</TabPanel>
						);
					})}
				</div>
			</ColumnLayout>
		</section>
	);
};
